import { useCallback, useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';

const calculateEffectiveOrientation = (): 'landscape' | 'portrait' => {
  const { wrapperType } = useStore.getState().runtime;
  const isMobile = wrapperType.isAndroid;
  const width = window.innerWidth;
  const height = window.innerHeight;

  if (!isMobile && width < 1000 && height > width) {
    return 'portrait';
  }

  if (screen.orientation?.type) {
    return screen.orientation.type.includes('landscape')
      ? 'landscape'
      : 'portrait';
  }

  return width > height ? 'landscape' : 'portrait';
};

export const useAppOrientation = () => {
  const [orientation, setOrientation] = useState<
    'landscape' | 'portrait' | undefined
  >(() => calculateEffectiveOrientation());

  const updateOrientation = useCallback(() => {
    const newOrientation = calculateEffectiveOrientation();
    setOrientation(newOrientation);
  }, []);

  useEffect(() => {
    // Listen to screen orientation changes (device rotation)
    const handleOrientationChange = () => {
      updateOrientation();
    };

    // Listen to window resize (desktop window resizing)
    const handleResize = () => {
      updateOrientation();
    };

    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleOrientationChange);
    }
    window.addEventListener('resize', handleResize);

    return () => {
      if (screen.orientation) {
        screen.orientation.removeEventListener(
          'change',
          handleOrientationChange,
        );
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [updateOrientation]);

  return orientation;
};

export type AppOrientation = 'landscape' | 'portrait' | undefined;
